import { Link } from "react-router-dom";
import { MapPin, Mail, Phone, Instagram, Facebook, Linkedin, Youtube } from "lucide-react";
import { track } from "@/lib/api";

const COLS = [
  {
    title: "Study Destinations",
    links: [
      ["United Kingdom", "/study-in-uk"],
      ["USA", "/study-in-usa"],
      ["Canada", "/study-in-canada"],
      ["Australia", "/study-in-australia"],
      ["Germany", "/study-in-germany"],
      ["Ireland", "/study-in-ireland"],
      ["Romania", "/study-in-romania"],
      ["All Destinations", "/destinations"],
    ],
  },
  {
    title: "Explore",
    links: [
      ["Find a Course", "/courses"],
      ["Find a University", "/universities"],
      ["Scholarships", "/scholarships"],
      ["Test Preparation", "/test-preparation"],
      ["Visa Guidance", "/visa-guidance"],
      ["Cost Calculator", "/cost-calculator"],
      ["Intake Planner", "/intake-planner"],
    ],
  },
  {
    title: "Company",
    links: [
      ["About Us", "/about"],
      ["Student Stories", "/student-stories"],
      ["Events", "/events"],
      ["Insights", "/insights"],
      ["Contact", "/contact"],
      ["FAQs", "/faq"],
    ],
  },
];

const LEGAL = [
  ["Privacy Policy", "/privacy"],
  ["Terms & Conditions", "/terms"],
  ["Cookie Policy", "/cookies"],
  ["Disclaimer", "/disclaimer"],
];

const SOCIALS = [
  { icon: Instagram, label: "Instagram", href: "https://www.instagram.com/harwellglobaleducation" },
  { icon: Facebook, label: "Facebook", href: "https://www.facebook.com/harwellglobaleducation" },
  { icon: Linkedin, label: "LinkedIn", href: "https://www.linkedin.com/company/harwellglobaleducation" },
  { icon: Youtube, label: "YouTube", href: "https://www.youtube.com/@harwellglobaleducation" },
];

export default function Footer() {
  return (
    <footer data-testid="site-footer" className="bg-[#F7F9FC] border-t border-[#E6EBF1] text-[#5F6B7A]">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-10">
          <div className="lg:col-span-4">
            <img src="/harwell-logo-transparent.png" alt="Harwell Global Education" className="h-16 w-auto object-contain mb-5" />
            <p className="font-editorial italic text-lg text-[#B45309]">Global Education. Informed Decisions. Global Futures.</p>
            <p className="text-sm text-[#5F6B7A] mt-3 leading-relaxed max-w-xs">
              Harwell Global Education is the international education vertical of Harwell Global — helping students
              explore the right country, university and course for their academic goals.
            </p>
            <div className="flex gap-3 mt-6">
              {SOCIALS.map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" aria-label={s.label}
                  data-testid={`footer-social-${s.label.toLowerCase()}`}
                  className="w-9 h-9 rounded-full bg-white border border-[#E6EBF1] flex items-center justify-center text-[#5F6B7A] hover:text-[#1E40AF] hover:border-[#1E40AF]/40 transition-all">
                  <s.icon className="w-4 h-4" />
                </a>
              ))}
            </div>
          </div>

          {COLS.map((col) => (
            <div key={col.title} className="lg:col-span-2">
              <h4 className="text-xs font-data uppercase tracking-[0.2em] text-slate-400 mb-4">{col.title}</h4>
              <ul className="space-y-2.5">
                {col.links.map(([label, to]) => (
                  <li key={label}>
                    <Link to={to} data-testid={`footer-link-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                      className="text-sm text-[#5F6B7A] hover:text-[#1E40AF] transition-colors">
                      {label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}

          <div className="lg:col-span-2">
            <h4 className="text-xs font-data uppercase tracking-[0.2em] text-slate-400 mb-4">Contact</h4>
            <ul className="space-y-3 text-sm">
              <li className="flex gap-2">
                <MapPin className="w-4 h-4 mt-0.5 shrink-0 text-[#1E40AF]" />
                <span>B-148, 1st Floor, B Block, Sector 63, Noida, Uttar Pradesh - 201301</span>
              </li>
              <li>
                <a href="mailto:Contact@harwellglobal.com" className="flex gap-2 hover:text-[#1E40AF] transition-colors" data-testid="footer-email-link">
                  <Mail className="w-4 h-4 shrink-0 text-[#1E40AF]" />
                  Contact@harwellglobal.com
                </a>
              </li>
              {["+91-120-4615329", "+91-9936706961", "+91-9289537180"].map((p) => (
                <li key={p}>
                  <a href={`tel:${p.replace(/-/g, "")}`} onClick={() => track("phone_click", { number: p })}
                    className="flex gap-2 hover:text-[#1E40AF] transition-colors" data-testid={`footer-phone-${p}`}>
                    <Phone className="w-4 h-4 shrink-0 text-[#1E40AF]" />
                    {p}
                  </a>
                </li>
              ))}
            </ul>
          </div>
        </div>

        <div className="mt-14 pt-7 border-t border-[#E6EBF1]">
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2 mb-5">
            {LEGAL.map(([label, to]) => (
              <Link key={label} to={to} data-testid={`footer-legal-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                className="text-xs text-slate-400 hover:text-[#1E40AF] transition-colors">
                {label}
              </Link>
            ))}
          </div>
          <p className="text-xs font-data tracking-wider text-slate-500 text-center">
            HARWELL GLOBAL &nbsp;•&nbsp; Education | Healthcare | Global Careers &nbsp;•&nbsp; harwellglobal.com
          </p>
          <p className="text-[11px] text-slate-400 text-center mt-2 max-w-2xl mx-auto">
            This website is dedicated to Harwell Global Education's international education services. Other Harwell
            Global verticals are listed for information only.
          </p>
          <p className="text-xs text-slate-400 text-center mt-4">© 2026 Harwell Global Education. All Rights Reserved.</p>
        </div>
      </div>
    </footer>
  );
}
